import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@payment-plan/ui';
import { companySchema, type CompanyFormValues } from './company.schema';
import { CompanyLogo, LogoMarco, useObjectUrl } from './CompanyLogo';
import { LOGO_ACCEPT, validarArchivoLogo } from './logo-archivo';
import { AYUDA_CODIGO, registroEnMayusculas } from '../codigo-consecutivo';
import {
  useCreateCompany,
  useRemoveCompanyLogo,
  useUpdateCompany,
  useUploadCompanyLogo,
} from './use-companies';
import type { Company } from '../../../types/admin';

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  mode?: 'view' | 'edit';
}

const EMPTY: CompanyFormValues = {
  codigo: '',
  nombre: '',
  nit: '',
  ciudad: '',
  correoContacto: '',
  notasPdf: '',
};

export function CompanyFormDialog({
  open,
  onOpenChange,
  company,
  mode = 'edit',
}: CompanyFormDialogProps): React.JSX.Element {
  const isView = mode === 'view';
  const isEdit = Boolean(company);
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const uploadLogo = useUploadCompanyLogo();
  const removeLogo = useRemoveCompanyLogo();
  const [serverError, setServerError] = React.useState<string | null>(null);
  // feat-66: el logo es un archivo aparte del JSON; se sube después de crear/editar.
  const [logoArchivo, setLogoArchivo] = React.useState<File | null>(null);
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const [quitarLogo, setQuitarLogo] = React.useState(false);
  // Si la inmobiliaria se creó pero el logo falló, el reintento edita la creada (no la duplica).
  const [creada, setCreada] = React.useState<Company | null>(null);
  const vistaPrevia = useObjectUrl(logoArchivo);
  const destino = company ?? creada;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
    defaultValues: EMPTY,
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setServerError(null);
    setLogoArchivo(null);
    setLogoError(null);
    setQuitarLogo(false);
    setCreada(null);
    reset(
      company
        ? {
            codigo: company.codigo,
            nombre: company.nombre,
            nit: company.nit,
            ciudad: company.ciudad,
            correoContacto: company.correoContacto,
            notasPdf: company.notasPdf ?? '',
          }
        : EMPTY,
    );
  }, [company, open, reset]);

  const elegirLogo = (archivo: File | undefined): void => {
    if (!archivo) return;
    const error = validarArchivoLogo(archivo);
    setLogoError(error);
    setLogoArchivo(error ? null : archivo);
    if (!error) setQuitarLogo(false);
  };

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    if (logoError) return;
    const payload = {
      nombre: values.nombre,
      ciudad: values.ciudad,
      correoContacto: values.correoContacto,
      ...(values.notasPdf ? { notasPdf: values.notasPdf } : {}),
    };
    let guardada: Company;
    try {
      if (destino) {
        // ADR-015: el código solo viaja si cambió (el API rechaza cambiarlo tras emitir documentos).
        const codigo = values.codigo !== destino.codigo ? { codigo: values.codigo } : {};
        guardada = await updateCompany.mutateAsync({ id: destino.id, data: { ...codigo, ...payload } });
      } else {
        guardada = await createCompany.mutateAsync({ ...payload, codigo: values.codigo, nit: values.nit });
      }
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'No fue posible guardar la inmobiliaria.',
      );
      return;
    }
    try {
      if (logoArchivo) {
        await uploadLogo.mutateAsync({ id: guardada.id, archivo: logoArchivo });
      } else if (quitarLogo && destino?.logo) {
        await removeLogo.mutateAsync(guardada.id);
      }
      onOpenChange(false);
    } catch (error) {
      if (!company) setCreada(guardada);
      const detalle = error instanceof Error ? error.message : 'intente de nuevo.';
      setServerError(`La inmobiliaria se guardó, pero el logo no: ${detalle}`);
    }
  });

  const logoVigente = destino?.logo && !quitarLogo ? destino : null;
  const vistaLogo = vistaPrevia ? (
    <LogoMarco src={vistaPrevia} alt="Vista previa del logo" nombre="Vista previa" size="md" />
  ) : logoVigente ? (
    <CompanyLogo company={logoVigente} size="md" />
  ) : (
    <LogoMarco src={null} alt="" nombre={destino?.nombre ?? 'Nueva'} size="md" />
  );

  const title = isView ? 'Ver inmobiliaria' : isEdit ? 'Editar inmobiliaria' : 'Nueva inmobiliaria';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isView
              ? 'Información de la inmobiliaria (solo lectura).'
              : 'Los campos marcados con * son obligatorios.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormField
            label="Código"
            htmlFor="company-codigo"
            required
            description={AYUDA_CODIGO}
            error={errors.codigo?.message}
          >
            <Input
              id="company-codigo"
              maxLength={3}
              autoCapitalize="characters"
              autoComplete="off"
              className="w-28 uppercase tracking-[0.22em]"
              error={Boolean(errors.codigo)}
              disabled={isView}
              {...registroEnMayusculas(register('codigo'))}
            />
          </FormField>
          <FormField label="Nombre" htmlFor="company-nombre" required error={errors.nombre?.message}>
            <Input
              id="company-nombre"
              error={Boolean(errors.nombre)}
              disabled={isView}
              {...register('nombre')}
            />
          </FormField>
          <FormField label="NIT" htmlFor="company-nit" required error={errors.nit?.message}>
            <Input
              id="company-nit"
              error={Boolean(errors.nit)}
              disabled={isEdit || isView}
              {...register('nit')}
            />
          </FormField>
          <FormField label="Ciudad" htmlFor="company-ciudad" required error={errors.ciudad?.message}>
            <Input
              id="company-ciudad"
              error={Boolean(errors.ciudad)}
              disabled={isView}
              {...register('ciudad')}
            />
          </FormField>
          <FormField
            label="Correo de contacto"
            htmlFor="company-correo"
            required
            error={errors.correoContacto?.message}
          >
            <Input
              id="company-correo"
              type="email"
              error={Boolean(errors.correoContacto)}
              disabled={isView}
              {...register('correoContacto')}
            />
          </FormField>
          <div className="flex items-start gap-4">
            {vistaLogo}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <FormField
                label="Logo"
                htmlFor="company-logo"
                description="PNG, JPG o SVG de hasta 2 MB. Se muestra en el PDF del plan de pagos."
                error={logoError ?? undefined}
              >
                <Input
                  id="company-logo"
                  type="file"
                  accept={LOGO_ACCEPT}
                  error={Boolean(logoError)}
                  disabled={isView}
                  onChange={(event) => elegirLogo(event.target.files?.[0])}
                />
              </FormField>
              {!isView && (logoArchivo || logoVigente) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => {
                    setLogoArchivo(null);
                    setLogoError(null);
                    setQuitarLogo(true);
                  }}
                >
                  Quitar logo
                </Button>
              )}
            </div>
          </div>
          <FormField label="Notas para el PDF" htmlFor="company-notas" error={errors.notasPdf?.message}>
            <Textarea id="company-notas" rows={3} disabled={isView} {...register('notasPdf')} />
          </FormField>
          {serverError && (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {isView ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!isView && (
              <Button type="submit" loading={isSubmitting}>
                Guardar
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
